import React, { useRef } from 'react';
import { IconListItem } from '@components/IconListItem/IconListItem';
import { useTranslation } from 'react-i18next';
import { motion, useInView } from 'framer-motion';

export const FeaturesSection = () => {
  const { t } = useTranslation();

  const dashRef = useRef(null);

  const isInView = useInView(dashRef, {
    margin: '0px 0px -100px 0px',
    once: false,
  });

  const fadeInUp = {
    animate: isInView ? { opacity: 1, y: 0 } : {},
    initial: { opacity: 0, y: 30 },
    transition: { duration: 0.6 },
  };

  const fadeIn = {
    animate: isInView ? { opacity: 1 } : {},
    initial: { opacity: 0 },
    transition: { duration: 0.6 },
  };

  const scaleIn = {
    animate: isInView ? { opacity: 1, scale: 1 } : {},
    initial: { opacity: 0, scale: 0.4 },
    transition: { duration: 1 },
  };

  return (
    <section
      ref={dashRef}
      id="features-dashboard"
      className="flex flex-col items-center gap-[20px] max-w-[1203px] mx-auto px-6"
    >
      <motion.div className="text-center lg:pb-8" {...fadeInUp}>
        <p className="text-xs font-semibold text-blue-600 tracking-widest uppercase">
          {t('features.title')}
        </p>
        <h2 className="text-[24px] sm:text-[40px] leading-[28px] sm:leading-[48px] font-bold text-black w-[300px] sm:w-[750px] h-[48px]">
          {t('features.subtitle')}
        </h2>
      </motion.div>

      <motion.div
        className="relative bg-[#1D3D59] text-white rounded-2xl p-8 md:p-12 mb-12 overflow-hidden h-auto lg:h-[509px] sm:w-full w-[80%] mx-[20px] sm:mx-6 lg:mx-0"
        {...scaleIn}
      >
        <h3 className="text-[18px] lg:text-[38px] leading-[36px] text-center font-semibold font-archivo mb-4">
          {t('features.dashboard.title')}
        </h3>
        <div className="lg:absolute w-full max-w-7xl lg:h-52 left-0 lg:top-[95px] font-inter font-normal text-[14px] lg:text-[18px] leading-[26px] text-center text-white mb-6 lg:mb-0">
          {t('features.dashboard.text')}
        </div>
        <div className="flex flex-col items-center gap-6 lg:gap-0 lg:block">
          <motion.div
            className="lg:absolute w-[400px] sm:w-[480px] left-[230px] lg:left-[351px] top-[130px] lg:top-[190px] mb-6 lg:mb-0"
            {...fadeIn}
          >
            <img
              src="/images/dashboard-rxs.png"
              alt={t('features.dashboard.title')}
              className="rounded-xl object-cover"
            />
          </motion.div>

          <motion.div
            className="w-[280px] h-[110px] bg-[#EDF6FB] rounded-2xl p-4 flex flex-col gap-2 text-gray-900 lg:absolute lg:left-[25px] lg:top-[140px]"
            {...fadeInUp}
          >
            <h4 className="font-inter font-bold text-lg leading-[10px] text-black">
              {t('features.settlements.title')}
            </h4>
            <ul className="list-disc list-inside text-lg font-normal space-y-1">
              <IconListItem>
                {t('features.settlements.items.auto')}
              </IconListItem>
              <IconListItem>
                {t('features.settlements.items.payment')}
              </IconListItem>
            </ul>
          </motion.div>

          <motion.div
            className="w-[300px] md:w-[290px] h-[120px] bg-[#EDF6FB] rounded-2xl p-4 flex flex-col gap-2 text-gray-900 lg:absolute lg:left-[828px] lg:top-[137px]"
            {...fadeInUp}
          >
            <h4 className="font-inter font-bold text-lg leading-[10px] text-black">
              {t('features.balances.title')}
            </h4>
            <ul className="list-disc list-inside text-lg font-normal space-y-1">
              <IconListItem>{t('features.balances.items.real')}</IconListItem>
              <IconListItem>{t('features.balances.items.status')}</IconListItem>
            </ul>
          </motion.div>

          <motion.div
            className="w-[300px] lg:w-[285px] h-[180px] bg-[#EDF6FB] rounded-2xl p-4 flex flex-col gap-2 text-gray-900 lg:absolute lg:left-[35px] lg:top-[310px]"
            {...fadeInUp}
          >
            <h4 className="font-inter font-bold text-lg leading-[10px] text-black">
              {t('features.traffic.title')}
            </h4>
            <ul className="list-disc list-inside text-lg font-normal">
              <IconListItem>
                {t('features.traffic.items.approval')}
              </IconListItem>
              <IconListItem>{t('features.traffic.items.average')}</IconListItem>
              <IconListItem>
                {t('features.traffic.items.rejection')}
              </IconListItem>
              <IconListItem>{t('features.traffic.items.refund')}</IconListItem>
            </ul>
          </motion.div>

          <motion.div
            className="w-[220px] bg-[#EDF6FB] rounded-2xl p-4 flex flex-col gap-2 text-gray-900 lg:absolute lg:left-[880px] lg:top-[286px]"
            {...fadeInUp}
          >
            <h4 className="font-inter font-bold text-lg leading-[10px] text-black">
              {t('features.reporting.title')}
            </h4>
            <ul className="list-disc list-inside text-lg font-normal space-y-1">
              <IconListItem>{t('features.reporting.items.payin')}</IconListItem>
              <IconListItem>
                {t('features.reporting.items.payout')}
              </IconListItem>
              <IconListItem>
                {t('features.reporting.items.refunds')}
              </IconListItem>
            </ul>
          </motion.div>
        </div>
      </motion.div>

      <motion.div
        className="bg-white grid grid-cols-1 lg:grid-cols-2 gap-6 w-full justify-center place-items-center lg:place-items-start"
        {...fadeIn}
      >
        <motion.div
          className="relative bg-[#F1F1F3] rounded-[24px] md:rounded-[32px] w-[328px] h-[180px]  md:w-[502px] md:h-[509px]"
          {...scaleIn}
        >
          <h4 className="absolute text-black font-archivo font-semibold text-[18px] leading-[28px]  w-[201px] left-[24px] top-[24px] md:text-[38px] md:leading-9 md:left-[40px] md:top-[56px] md:w-[460px]">
            {t('features.cards.fraud.title')}
          </h4>
          <p className="absolute text-[#727272] font-inter text-[14px] leading-[18px] w-[201px] left-[24px] top-[80px] md:text-[18px] md:leading-[26px] md:w-[390px] md:left-[40px] md:top-[130px]">
            {t('features.cards.fraud.text')}
          </p>
          <img
            src="/images/fraud-monitoring.png"
            alt={t('features.cards.fraud.title')}
            className="absolute w-[80px] h-auto left-[73.48%] top-[5.77%] md:w-[307px] md:h-[288px] md:left-[98px] md:top-[206px]"
          />
        </motion.div>

        <motion.div
          className="relative bg-[#EDF6FB] rounded-[24px] md:rounded-[32px] w-[328px] h-[180px] md:w-[502px] md:h-[460px] lg:w-[550px] lg:h-[509px]"
          {...scaleIn}
        >
          <h4 className="absolute text-black font-archivo font-semibold text-[18px] leading-[28px] left-[24px] top-[24px] md:text-[38px] md:leading-9 md:left-[40px] md:top-[56px] md:w-[500px]">
            {t('features.cards.traffic.title')}
          </h4>
          <p className="absolute text-[#727272] font-inter text-[14px] leading-[18px] w-[240px] left-[24px] top-[60px] md:text-[18px] md:leading-[26px] md:w-[450px] md:left-[40px] md:top-[130px]">
            {t('features.cards.traffic.text')}
          </p>
          <img
            src="/images/traffic.png"
            alt={t('features.cards.traffic.title')}
            className="absolute w-[80px] h-auto left-[73.48%] top-[5.77%] sm:w-[90%] sm:left-[40px] md:top-[206px]"
          />
        </motion.div>
        <motion.div className="grid grid-cols-1 gap-4" {...fadeIn}>
          <motion.div
            className="relative bg-[#EDF6FB] rounded-[24px] md:rounded-[32px] w-[328px] h-[120px] md:w-[502px] md:h-[200px] lg:w-[502px] lg:h-[189px]"
            {...scaleIn}
          >
            <h4 className="absolute text-black font-archivo font-semibold text-[18px] leading-[28px] left-[24px] top-[24px] md:text-[38px] md:leading-9 md:left-[40px] md:top-[56px] md:w-[183px]">
              {t('features.cards.automation.title')}
            </h4>
            <p className="absolute text-[#727272] font-inter text-[14px] leading-[18px] w-[240px] left-[24px] top-[60px] md:text-[18px] md:leading-[26px] md:w-[384px] md:left-[40px] md:top-[116px]">
              {t('features.cards.automation.text')}
            </p>
            <img
              src="/images/automation.png"
              alt={t('features.cards.automation.title')}
              className="absolute w-[80px] h-auto left-[73.48%] top-[5.77%] sm:w-[103px] sm:left-[365px] md:top-[19px]"
            />
          </motion.div>
          <motion.div
            className="relative bg-[#EDF6FB] rounded-[24px] md:rounded-[32px] w-[328px] h-[150px] md:w-[502px] md:h-[200px] lg:w-[502px] lg:h-[245px]"
            {...scaleIn}
          >
            <h4 className="absolute text-black font-archivo font-semibold text-[18px] leading-[28px] left-[24px] top-[24px] md:text-[34px] md:leading-9 md:left-[40px] md:top-[56px] md:w-[183px]">
              {t('features.cards.autoscaling.title')}
            </h4>
            <p className="absolute text-[#727272] font-inter text-[14px] leading-[18px] w-[240px] left-[24px] top-[85px] md:text-[18px] md:leading-[26px] md:w-[384px] md:left-[40px] md:top-[140px]">
              {t('features.cards.autoscaling.text')}
            </p>
            <img
              src="/images/autoscaling.png"
              alt={t('features.cards.autoscaling.title')}
              className="absolute w-[80px] h-auto left-[73.48%] top-[5.77%] sm:w-[127px] sm:left-[365px] md:top-[19px]"
            />
          </motion.div>
        </motion.div>
        <motion.div
          className="relative bg-[#F1F1F3] rounded-[24px] md:rounded-[32px] w-[328px] h-[200px] md:w-[502px] md:h-[380px] lg:w-[550px] lg:h-[400px]"
          {...scaleIn}
        >
          <h4 className="absolute text-black font-archivo font-semibold text-[18px] leading-[28px] left-[24px] top-[24px] md:text-[38px] md:leading-9 md:left-[40px] md:top-[56px] md:w-[400px]">
            {t('features.cards.whitelabel.title')}
          </h4>
          <p className="absolute text-[#727272] font-inter text-[14px] leading-[18px] w-[210px] left-[24px] top-[60px] md:text-[18px] md:leading-[26px] md:w-[390px] md:left-[40px] md:top-[116px]">
            {t('features.cards.whitelabel.text')}
          </p>
          <img
            src="/images/whitelabel.png"
            alt={t('features.cards.whitelabel.title')}
            className="absolute w-[80px] h-auto left-[73.48%] top-[5.77%] sm:w-[147px] sm:left-[350px] md:top-[250px]"
          />
        </motion.div>
      </motion.div>
    </section>
  );
};
